/*************************************************
  1Store — Auto image fit v8.3
  Автоматически приводит товары к единому визуальному размеру и
  центрирует их: меряет реальные границы товара (непустые пиксели)
  и выставляет per-image переменные --img-zoom / --img-dx / --img-dy.

  Зоны действия (CONTEXTS):
  - карточки каталога:  ul.l4cl li.product-card figure
    (обе картинки: основная И ховерная — каждая со своим расчётом)
  - галерея карточки товара: .m6pr .l4pr > li
    (миниатюры-«буллеты» не трогаем)

  Приоритеты: автоматика главнее метаполя custom.image_zoom
  (метаполе на figure остаётся запасным: работает через наследование
  CSS-переменной, когда анализ невозможен — серый фон и т.п.).

  Экономия: анализ по миниатюре width=180 (~3-8 КБ), результат в
  localStorage по имени файла (замена фото в Shopify = новое имя =
  свежий анализ, протухнуть не может). Расчёт от offset-размеров и
  натуральных пикселей — transform их не искажает, петли нет.

  iOS Safari: запрос анализа идёт с отдельными URL-параметрами,
  иначе Safari отдаёт кэш без CORS-заголовков и canvas блокируется.

  Отладка: открыть страницу с ?imgfit-debug — плашки на карточках.

  v8 (PSI 20.09.2026) — математика зума, кэш, атрибуты data-imgfit-*
  и CSS-переменные НЕ менялись; результат на экране тот же. Изменено
  только КАК и КОГДА считаем:
  1. Догрузка более чёткого кандидата srcset (img.sizes) — только если
     уже загруженный кандидат реально мельче, чем нужно после зума.
     Раньше sizes переставлялся всегда при zoom > 1.05, браузер
     перекачивал главное фото галереи вторым запросом, и именно этот
     второй запрос становился LCP (PSI: 8,4 с на мобиле, «запрос не
     найден в исходном документе»). В паре с запасом ×2 в sizes из
     main-product-gallery.liquid повторной загрузки не бывает вовсе.
  2. Чтение геометрии (offsetWidth…) и запись стилей разнесены по
     фазам внутри одного кадра: сначала меряем все картинки, потом
     пишем всем. Раньше read→write→read шло по каждой картинке подряд.
     Честно: в аудите PSI на долю этого файла пришлось ~1 мс
     принудительной компоновки — это гигиена, а не главный выигрыш;
     основной Style & Layout страницы даёт Swiper в scripts.js.
  3. Canvas-анализ выполняется по одному, с уступкой потока между
     картинками (requestIdleCallback), сеть при этом параллельная.
  4. Опрос setInterval раз в секунду заменён на MutationObserver:
     реагируем на реально вставленные узлы (клоны свайпера, догрузка
     сетки), а не обходим все картинки страницы по таймеру.
  v8.1: одновременные запросы анализа одного файла склеиваются
     (inflight по ключу кэша) и картинка в работе второй раз не
     берётся — иначе load + обход + observer могли запустить два-три
     canvas-анализа одной картинки.
  v8.2: в фоновой вкладке (visibilityState hidden) запись стилей идёт
     через setTimeout(0), а не requestAnimationFrame — иначе подгонка
     ждала переключения на вкладку (найдено на превью черновика).
  v8.3: там же, в фоновой вкладке, canvas-анализ не ждёт
     requestIdleCallback (браузер душит его до 1/с и 1/мин) — идёт по
     микрозадачам, как считал v7; уступка потока остаётся для видимой.
**************************************************/
(() => {
  const TARGET = 0.9;          // товар должен занимать ~90% бокса
  const MIN = 0.8, MAX = 2.0;  // пределы авто-зума (MAX = запас sizes в Liquid)
  const SKIP_DELTA = 0.03;     // если и так почти идеально — не трогаем
  const SAMPLE = 96;           // ширина канвы для анализа (пикс)
  const CACHE = 'imgbox2:';
  const CONTEXTS = 'ul.l4cl li.product-card figure, .m6pr .l4pr li:not(.more)';
  // v8: допуск при решении о догрузке. Кандидаты srcset дискретны
  // (…803w, 1085w, 1416w); нехватка < 5% плотности на телефоне не видна,
  // а лишний запрос главного фото стоит секунды LCP.
  const SHARP_TOLERANCE = 0.95;

  /* ── анализ границ товара ───────────────────────────────────────── */

  // сеть: миниатюра грузится параллельно с остальными
  const loadThumb = (src) => new Promise((resolve) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve(im);
    im.onerror = () => resolve(null);
    if (src.indexOf('/cdn/shop/') > -1) {
      im.src = src.split('?')[0] + '?width=180&imgfit=cors';
    } else {
      im.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'imgfit=cors';
    }
  });

  // CPU: canvas-анализ — по одному, между картинками уступаем поток.
  // v8.3: уступать есть смысл только видимой вкладке. В фоновой браузер
  // душит и таймеры, и idle-коллбэки (до одного в секунду, после пяти
  // минут — до одного в минуту): на коллекции анализ полусотни карточек
  // растянулся бы на минуту, и при переключении на вкладку картинки
  // «доезжали» бы на глазах. v7 в фоне считал сразу — делаем так же:
  // в hidden очередь идёт по микрозадачам, без ожидания кадра.
  let cpuSlot = Promise.resolve();
  const idle = window.requestIdleCallback
    ? (fn) => requestIdleCallback(fn, { timeout: 250 })
    : (fn) => setTimeout(fn, 0);
  const yieldIfVisible = () => (document.visibilityState === 'hidden'
    ? Promise.resolve()
    : new Promise((r) => idle(r)));
  const takeSlot = () => {
    const mine = cpuSlot.then(yieldIfVisible);
    cpuSlot = mine.catch(() => {});
    return mine;
  };

  function measureBox(im) {
    try {
      const w = SAMPLE;
      const h = Math.max(1, Math.round((w * im.naturalHeight) / im.naturalWidth));
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(im, 0, 0, w, h);
      const d = ctx.getImageData(0, 0, w, h).data;
      let minX = w, minY = h, maxX = -1, maxY = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          if (d[i + 3] > 20 && !(d[i] > 244 && d[i + 1] > 244 && d[i + 2] > 244)) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return null;
      return {
        fw: Math.round(((maxX - minX + 1) / w) * 1000) / 1000,
        fh: Math.round(((maxY - minY + 1) / h) * 1000) / 1000,
        cx: Math.round((((minX + maxX + 1) / 2) / w) * 1000) / 1000,
        cy: Math.round((((minY + maxY + 1) / 2) / h) * 1000) / 1000,
      };
    } catch (e) {
      return null;
    }
  }

  async function analyze(src) {
    const im = await loadThumb(src);
    if (!im) return null;
    await takeSlot();
    return measureBox(im);
  }

  const cacheKey = (src) => CACHE + src.split('?')[0].split('/').pop();

  // v8.1: один файл — один анализ. Пока анализ идёт, повторные запросы
  // того же файла (load + первый обход + observer часто приходят в одну
  // сотню миллисекунд; одна и та же картинка может стоять в нескольких
  // боксах) получают тот же promise, а не новый canvas.
  const inflight = new Map(); // cacheKey -> Promise<box|null>
  function getBox(src) {
    const k = cacheKey(src);
    try {
      const c = JSON.parse(localStorage.getItem(k));
      if (c && c.fw > 0 && c.fh > 0) return Promise.resolve(c);
    } catch (e) {}
    if (inflight.has(k)) return inflight.get(k);
    const p = analyze(src).then((a) => {
      if (a) { try { localStorage.setItem(k, JSON.stringify(a)); } catch (e) {} }
      return a || null;
    }).finally(() => { inflight.delete(k); });
    inflight.set(k, p);
    return p;
  }

  /* ── ширина уже загруженного кандидата srcset (в device px) ─────── */
  function loadedCandidateWidth(img) {
    const cur = img.currentSrc || '';
    const srcset = img.getAttribute('srcset') || '';
    if (!cur || !srcset) return { loaded: 0, max: 0 };
    const norm = (u) => u.trim().replace(/^https?:/, '').replace(/&amp;/g, '&');
    const want = norm(cur);
    let loaded = 0, max = 0;
    srcset.split(',').forEach((part) => {
      const m = part.trim().match(/^(\S+)\s+(\d+)w$/);
      if (!m) return;
      const w = parseInt(m[2], 10);
      if (w > max) max = w;
      if (norm(m[1]) === want) loaded = w;
    });
    return { loaded, max };
  }

  /* ── фаза чтения: вся геометрия, никаких записей в DOM ──────────── */
  function measure(container, img, box) {
    const natW = img.naturalWidth, natH = img.naturalHeight;
    if (!natW || !natH) return null;

    const pic = img.closest('picture') || img.parentElement;
    const boxW = container.offsetWidth, boxH = container.offsetHeight;
    const picW = pic ? pic.offsetWidth : boxW, picH = pic ? pic.offsetHeight : boxH;
    const imgW = img.offsetWidth, imgH = img.offsetHeight;
    if (!boxW || !boxH || !picW || !picH || !imgW || !imgH) return null;

    // видимая область = самый тесный контейнер с обрезкой
    const clipW = Math.min(boxW, picW);
    const clipH = Math.min(boxH, picH);

    // как object-fit:contain отрисует картинку внутри её элемента
    const paint = Math.min(imgW / natW, imgH / natH);
    const contentW = natW * paint * box.fw;
    const contentH = natH * paint * box.fh;
    if (!contentW || !contentH) return null;

    const fill = Math.max(contentW / clipW, contentH / clipH);
    if (!fill) return null;

    // на первом фото карточки товара с габаритами оставляем запас
    // под размерные линии (product-dimensions.js)
    let target = TARGET;
    if (container.closest && container.closest('.l4pr[data-dims-h]')) target = 0.84;

    let zoom = target / fill;
    zoom = Math.min(zoom, MAX);
    zoom = Math.max(zoom, MIN);
    zoom = Math.round(zoom * 100) / 100;
    if (Math.abs(zoom - 1) <= SKIP_DELTA) zoom = 1;

    // центрирование товара, смещённого в кадре
    const paintedW = natW * paint, paintedH = natH * paint;
    let dx = 0, dy = 0;
    if (box.cx && box.cy) {
      dx = Math.round(-(box.cx - 0.5) * paintedW * zoom);
      dy = Math.round(-(box.cy - 0.5) * paintedH * zoom);
    }

    // v8: догружать чёткий вариант только когда загруженного не хватает.
    // Если загружен уже самый крупный кандидат — просить нечего.
    let wantSizes = 0;
    if (zoom > 1.05 && img.getAttribute('sizes')) {
      const want = Math.ceil(imgW * zoom);
      const cur = parseInt(img.dataset.imgfitSizes || '0', 10);
      if (want > cur) {
        const { loaded, max } = loadedCandidateWidth(img);
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        const need = max ? Math.min(want * dpr, max) : want * dpr;
        if (!loaded || loaded < need * SHARP_TOLERANCE) wantSizes = want;
      }
    }

    return { img, container, box, fill, zoom, dx, dy, wantSizes };
  }

  /* ── фаза записи: только стили и data-атрибуты ───────────────────── */
  function commit(p) {
    const { img, container, box, fill, zoom, dx, dy, wantSizes } = p;
    // рамка пишется здесь же, в фазе записи: product-dimensions.js ждёт именно
    // этот атрибут и сразу читает геометрию — пусть видит уже готовые стили
    img.dataset.imgfitBox = JSON.stringify(box);
    img.dataset.imgfitFill = fill.toFixed(2);

    const sig = zoom + '|' + dx + '|' + dy;
    if (img.dataset.imgfitSig !== sig) {
      img.dataset.imgfitSig = sig;
      img.dataset.imgfitZoom = String(zoom);
      // переменные на САМОМ img: у основной и ховерной картинки — свои,
      // а метаполе на figure остаётся наследуемым запасным значением
      img.style.setProperty('--img-zoom', zoom);
      img.style.setProperty('--img-dx', dx + 'px');
      img.style.setProperty('--img-dy', dy + 'px');
    }
    if (wantSizes) {
      img.dataset.imgfitSizes = String(wantSizes);
      img.sizes = wantSizes + 'px';
    }
    if (container.querySelector('img') === img) {
      mark(container, 'fill ' + fill.toFixed(2) + ' → ×' + zoom);
    }
  }

  // очередь «применить»: все чтения → все записи, один раз за кадр.
  // v8.2: в фоновой вкладке requestAnimationFrame не срабатывает вовсе
  // (открыли товар в новой вкладке — подгонка ждала бы переключения и
  // «прыгала» на глазах). Там же — setTimeout(0); при уходе вкладки в фон
  // уже запланированный кадр переносится на таймер.
  const pending = new Map(); // img -> { container, box }
  let flushHandle = 0, flushKind = '';
  function scheduleFlush() {
    if (flushHandle) return;
    if (document.visibilityState === 'hidden') {
      flushKind = 'timer';
      flushHandle = setTimeout(flush, 0);
    } else {
      flushKind = 'raf';
      flushHandle = requestAnimationFrame(flush);
    }
  }
  function requestApply(container, img, box) {
    pending.set(img, { container, box });
    scheduleFlush();
  }
  // картинка ещё без раскладки (слайд 0×0 до инициализации свайпера,
  // свёрнутый блок) — v7 добирал такие опросом по таймеру; здесь повторяем
  // только для неё и не более трёх раз
  const retries = new WeakMap();
  function flush() {
    flushHandle = 0;
    flushKind = '';
    const jobs = Array.from(pending.entries());
    pending.clear();
    const plans = [];
    for (let i = 0; i < jobs.length; i++) {
      const img = jobs[i][0], job = jobs[i][1];
      if (!img.isConnected) continue;
      const p = measure(job.container, img, job.box);
      if (p) { plans.push(p); continue; }
      const n = retries.get(img) || 0;
      if (n < 3) {
        retries.set(img, n + 1);
        setTimeout(() => requestApply(job.container, img, job.box), 400 * (n + 1));
      }
    }
    for (let i = 0; i < plans.length; i++) commit(plans[i]);
  }

  /* отладка: страница с ?imgfit-debug — плашки со статусом */
  const DEBUG = location.search.indexOf('imgfit-debug') > -1;
  function mark(container, text, bad) {
    if (!DEBUG) return;
    let el = container.querySelector(':scope > .imgfit-debug');
    if (!el) {
      el = document.createElement('span');
      el.className = 'imgfit-debug';
      el.style.cssText = 'position:absolute;left:4px;bottom:4px;z-index:99;font:700 11px/1.6 sans-serif;padding:0 7px;border-radius:6px;background:#0e7a3d;color:#fff;pointer-events:none';
      container.style.position = 'relative';
      container.appendChild(el);
    }
    el.style.background = bad ? '#c8382e' : '#0e7a3d';
    el.textContent = text;
  }

  function eligible(img) {
    if (!img || img.tagName !== 'IMG') return null;
    if (img.closest('.swiper-pagination-bullets')) return null; // миниатюры галереи
    return img.closest(CONTEXTS);
  }

  // v8.1: картинка, для которой подгонка уже идёт (тот же src), второй
  // раз в работу не берётся — раньше окно между записью imgfitSrc и
  // появлением imgfitSig пропускало повторный вызов.
  const fitting = new WeakSet();
  async function fit(container, img) {
    const src = img.currentSrc || img.src;
    if (!src || !img.complete || !img.naturalWidth) return;
    if (img.dataset.imgfitSrc === src && (img.dataset.imgfitSig || fitting.has(img))) return;
    img.dataset.imgfitSrc = src;
    fitting.add(img);

    let box = null;
    try { box = await getBox(src); } finally { fitting.delete(img); }
    if (!box) {
      if (DEBUG) {
        const manual = parseFloat(getComputedStyle(container).getPropertyValue('--img-zoom')) || 1;
        mark(container, Math.abs(manual - 1) > 0.001 ? 'manual ' + manual : 'ERR analyze', Math.abs(manual - 1) <= 0.001);
      }
      return; // остаётся наследуемое значение метаполя (или 1)
    }
    requestApply(container, img, box);
  }

  // картинки по мере загрузки (фаза перехвата: load у img не всплывает)
  document.addEventListener('load', (e) => {
    const container = eligible(e.target);
    if (container) fit(container, e.target);
  }, true);

  function sweep(root) {
    const scope = root || document;
    const containers = (scope.matches && scope.matches(CONTEXTS))
      ? [scope]
      : scope.querySelectorAll(CONTEXTS);
    containers.forEach((container) => {
      container.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.naturalWidth && eligible(img) === container) fit(container, img);
      });
    });
  }

  // v8: вместо опроса по таймеру — реагируем на вставленные узлы
  // (клоны свайпера, догруженная сетка, смена варианта). Узел внутри
  // существующего бокса → пересмотреть этот бокс; иначе — искать боксы
  // внутри вставленного.
  function sweepAdded(node) {
    if (!node || node.nodeType !== 1) return;
    const own = node.closest ? node.closest(CONTEXTS) : null;
    if (own) { sweep(own); return; }
    sweep(node);
  }
  const moBuf = new Set();
  let moTimer = 0;
  const mo = new MutationObserver((records) => {
    for (let i = 0; i < records.length; i++) {
      const added = records[i].addedNodes;
      for (let j = 0; j < added.length; j++) moBuf.add(added[j]);
    }
    if (moTimer) return;
    moTimer = setTimeout(() => {
      moTimer = 0;
      const nodes = Array.from(moBuf);
      moBuf.clear();
      nodes.forEach(sweepAdded);
    }, 50);
  });

  /* пересчёт при смене раскладки (поворот телефона, ресайз окна) */
  let rt = null;
  window.addEventListener('resize', () => {
    clearTimeout(rt);
    rt = setTimeout(() => {
      document.querySelectorAll('img[data-imgfit-box]').forEach((img) => {
        const container = eligible(img);
        if (!container || !img.complete) return;
        try { requestApply(container, img, JSON.parse(img.dataset.imgfitBox)); } catch (e) {}
      });
    }, 200);
  });

  const boot = () => {
    sweep();
    mo.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // страховки вместо опроса: одна после полной загрузки (в простое),
  // возврат из bfcache, возврат на вкладку
  window.addEventListener('load', () => idle(() => sweep()));
  window.addEventListener('pageshow', (e) => { if (e.persisted) sweep(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && flushKind === 'raf') {
      cancelAnimationFrame(flushHandle);
      flushHandle = 0;
      scheduleFlush();
    }
    if (document.visibilityState === 'visible') sweep();
  });
})();
